import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Box, Text, VStack } from '@chakra-ui/react';

/** Same face palette as the home-page floating cube. */
const CUBE_FACE_COLORS = [0x80bcbd, 0xaad9bb, 0xd5f0c1, 0xf9f7c9, 0xd9edbf, 0xdcffb7] as const;

export interface RotatingCubeLoaderProps {
  /** Optional caption under the cube */
  label?: string;
  /** Canvas size in CSS pixels */
  size?: number;
  /** Fill available height (window body) vs compact min height */
  fullHeight?: boolean;
}

/**
 * Small Three.js rotating cube loading indicator, styled like the desktop cube.
 */
export function RotatingCubeLoader({
  label,
  size = 104,
  fullHeight = true,
}: RotatingCubeLoaderProps) {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) {
      return;
    }

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.z = 3.15;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(size, size);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const geometry = new THREE.BoxGeometry(1.25, 1.25, 1.25);
    const materials = CUBE_FACE_COLORS.map((color) => new THREE.MeshBasicMaterial({ color }));
    const cube = new THREE.Mesh(geometry, materials);
    // Match home cube spin feel (slight initial tilt)
    cube.rotation.x = 0.35;
    cube.rotation.y = 0.55;
    scene.add(cube);

    let frameId = 0;
    let running = true;

    const animate = () => {
      if (!running) {
        return;
      }
      cube.rotation.x += 0.01;
      cube.rotation.y += 0.01;
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      running = false;
      cancelAnimationFrame(frameId);
      geometry.dispose();
      materials.forEach((material) => material.dispose());
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [size]);

  return (
    <VStack
      className="rotating-cube-loader"
      spacing={5}
      align="center"
      justify="center"
      w="100%"
      h={fullHeight ? '100%' : 'auto'}
      minH={fullHeight ? '40%' : undefined}
      py={8}
      bg="transparent"
    >
      <Box
        ref={mountRef}
        w={`${size}px`}
        h={`${size}px`}
        flexShrink={0}
        lineHeight={0}
        aria-hidden
      />
      {label ? (
        <Text
          fontFamily="Roboto, sans-serif"
          color="#9fa8c1"
          fontSize="0.95rem"
          m={0}
          textAlign="center"
        >
          {label}
        </Text>
      ) : null}
    </VStack>
  );
}

export default RotatingCubeLoader;
